import { Button } from "components/DesignSystem/Button/Button";
import { Form, Formik, useFormikContext } from "formik";
import { useToast } from "hooks/useToast";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { useEffect, useRef } from "react";
import { useUpdateEntityCrmFieldsMutation } from "store/apis/crmFields";
import { CrmField, CrmSection, CrmTab } from "types/Models/crmFields";
import { CRMSectionCard } from "./CRMSectionCard";
import { buildChangedValues, buildInitialValues } from "./crmFormUtils";
import { useCollapsedCrmSections } from "./useCollapsedCrmSections";

const SyncSectionsOnServerChange = ({
  sections,
  values,
}: {
  sections: CrmSection[];
  values: Record<string, any>;
}) => {
  const { resetForm } = useFormikContext<Record<string, any>>();
  const valuesRef = useRef(values);
  valuesRef.current = values;

  useEffect(() => {
    if (buildChangedValues(sections, valuesRef.current).length === 0) {
      resetForm({ values: buildInitialValues(sections) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  return null;
};

/**
 * One CRM tab. Fields are editable straight away - the save bar only appears once something
 * has actually changed, and only the changed fields are sent so two people editing different
 * sections of the same tab do not clobber each other.
 */
export const CRMTabPanel = ({
  tab,
  entityId,
  isAdmin,
  onAddSection,
  onEditSection,
  onDeleteSection,
  onAddField,
  onEditField,
  onRemoveField,
  onViewAuditLog,
}: {
  tab: CrmTab;
  entityId: string;
  isAdmin: boolean;
  onAddSection: (tab: CrmTab) => void;
  onEditSection: (section: CrmSection) => void;
  onDeleteSection: (section: CrmSection) => void;
  onAddField: (section: CrmSection) => void;
  onEditField: (field: CrmField) => void;
  onRemoveField: (section: CrmSection, field: CrmField) => void;
  onViewAuditLog: (field: CrmField) => void;
}) => {
  const { alertToast, successToast } = useToast();
  const { isCollapsed, toggle, collapseAll, expandAll } =
    useCollapsedCrmSections();
  const [updateValues, { isLoading: isSaving }] =
    useUpdateEntityCrmFieldsMutation();
  const sectionIds = tab.sections.map((section) => section.uuid);

  const onSubmit = async (formValues: Record<string, any>) => {
    const values = buildChangedValues(tab.sections, formValues);
    if (values.length === 0) {
      return;
    }

    try {
      await updateValues({ entityId, values }).unwrap();
      successToast({ message: `${tab.title} updated` });
    } catch (error: any) {
      alertToast({ message: error?.data?.message || "Could not save changes" });
    }
  };

  if (tab.sections.length === 0) {
    return (
      <div className="t-flex t-flex-col t-gap-4 t-items-center">
        <EmptyScreen text="This tab has no sections yet" />
        {isAdmin && (
          <Button size="small" onClick={() => onAddSection(tab)}>
            Add section
          </Button>
        )}
      </div>
    );
  }

  return (
    <Formik
      initialValues={buildInitialValues(tab.sections)}
      onSubmit={onSubmit}
    >
      {({ values, submitForm, resetForm }) => {
        const changedCount = buildChangedValues(tab.sections, values).length;

        return (
          <Form className="t-m-0 t-flex t-flex-col t-gap-3">
            <SyncSectionsOnServerChange
              sections={tab.sections}
              values={values}
            />
            <div className="t-flex t-justify-end t-gap-2">
              <Button
                size="small"
                customType="ghost"
                type="button"
                onClick={() => expandAll(sectionIds)}
              >
                Expand all
              </Button>
              <Button
                size="small"
                customType="ghost"
                type="button"
                onClick={() => collapseAll(sectionIds)}
              >
                Collapse all
              </Button>
            </div>

            <div className="t-flex t-flex-col t-gap-3">
              {tab.sections.map((section) => (
                <CRMSectionCard
                  key={section.uuid}
                  section={section}
                  entityId={entityId}
                  isAdmin={isAdmin}
                  isOpen={!isCollapsed(section.uuid)}
                  onOpenChange={(shouldOpen) =>
                    toggle(section.uuid, shouldOpen)
                  }
                  unsavedCount={buildChangedValues([section], values).length}
                  onAddField={onAddField}
                  onEditField={onEditField}
                  onRemoveField={onRemoveField}
                  onEditSection={onEditSection}
                  onDeleteSection={onDeleteSection}
                  onViewAuditLog={onViewAuditLog}
                />
              ))}
            </div>

            {isAdmin && (
              <div className="t-flex t-justify-start">
                <Button
                  size="small"
                  customType="ghost"
                  type="button"
                  onClick={() => onAddSection(tab)}
                >
                  Add section
                </Button>
              </div>
            )}

            {changedCount > 0 && (
              <div className="t-sticky t-bottom-0 t-z-10 t-flex t-items-center t-justify-end t-gap-3 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-bg-white t-p-3 t-shadow-top-shadow">
                <span className="t-text-body-sm t-text-text-30">
                  {changedCount} unsaved{" "}
                  {changedCount === 1 ? "change" : "changes"}
                </span>
                <Button
                  size="small"
                  type="button"
                  onClick={() => resetForm()}
                  disabled={isSaving}
                >
                  Discard
                </Button>
                <Button
                  size="small"
                  customType="primary"
                  type="button"
                  onClick={submitForm}
                  isLoading={isSaving}
                  disabled={isSaving}
                >
                  Save
                </Button>
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
